import { useCallback, useEffect, useState } from 'react';

interface FnoSymbolsPayload {
  source?: string;
  generatedAt?: string;
  count?: number;
  symbols?: string[];
}

let cachedSymbols: Set<string> | null = null;
let inFlightRequest: Promise<Set<string>> | null = null;

const normalizeSymbol = (symbol: string) => symbol.trim().toUpperCase();

const loadFnoSymbols = async (): Promise<Set<string>> => {
  if (cachedSymbols) {
    return cachedSymbols;
  }

  if (!inFlightRequest) {
    inFlightRequest = fetch('/fno-symbols.json')
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to fetch F&O symbols: HTTP ${response.status}`);
        }

        const payload = (await response.json()) as FnoSymbolsPayload;
        const symbols = new Set((payload.symbols ?? []).map(normalizeSymbol));
        cachedSymbols = symbols;
        return symbols;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }

  return inFlightRequest;
};

export const useFnoSymbols = () => {
  const [symbols, setSymbols] = useState<Set<string>>(cachedSymbols ?? new Set<string>());
  const [loading, setLoading] = useState<boolean>(!cachedSymbols);

  useEffect(() => {
    if (cachedSymbols) {
      setSymbols(cachedSymbols);
      setLoading(false);
      return;
    }

    let isMounted = true;

    loadFnoSymbols()
      .then((loadedSymbols) => {
        if (isMounted) {
          setSymbols(loadedSymbols);
        }
      })
      .catch((error) => {
        console.error('Failed to load F&O symbols', error);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const isFnoSymbol = useCallback(
    (symbol?: string | null) => {
      if (!symbol) {
        return false;
      }

      return symbols.has(normalizeSymbol(symbol));
    },
    [symbols]
  );

  return {
    symbols,
    loading,
    isFnoSymbol,
  };
};
